import { Box, Button, Checkbox, Typography } from "@mui/material";
import { CheckboxIcon, CheckboxOutlineIcon } from "app/icons";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectVehicleDetailsForm } from "../../../redux/selector";
import { actions } from "../../../redux/slice";
import {
  VEHICLE_DRIVER_CAB_OPTIONS,
  VEHICLE_DRIVE_OPTIONS,
  VEHICLE_REAR_SUSPENSION_OPTIONS,
  VehicleDetailsFormValues,
} from "../../../types";
import {
  CHASSIS_DATA_FIELDS,
  VEHICLE_CONFIG_FIELDS,
  VEHICLE_DATA_FIELDS,
  VEHICLE_DETAILS_FORM_I18N_PREFIX,
  VEHICLE_HYDRAULIC_FIELD,
  WEIGHT_DATA_FIELDS,
} from "../data";
import {
  FormFieldControl,
  FormSection,
  renderFieldControls,
} from "./FormControls";
import { useVehicleDetails } from "./hooks/useVehicleDetails";
import { useStyles } from "./styles";

const VehicleDetails = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const formValues = useSelector(selectVehicleDetailsForm);
  const {
    manufacturerOptions,
    manufacturerModelOptions,
    hydraulicOptions,
    onManufacturerMenuScroll,
    onManufacturerModelMenuScroll,
  } = useVehicleDetails();
  const formPrefix = VEHICLE_DETAILS_FORM_I18N_PREFIX;

  const updateField = (
    field: keyof VehicleDetailsFormValues,
    value: string | boolean
  ) => {
    dispatch(actions.updateVehicleDetailsFormField({ field, value }));
  };

  useEffect(() => {
    if (!formValues || formValues.hydraulic || !hydraulicOptions[0]) {
      return;
    }
    dispatch(
      actions.updateVehicleDetailsFormField({
        field: "hydraulic",
        value: hydraulicOptions[0][0],
      })
    );
  }, [dispatch, formValues, hydraulicOptions]);

  useEffect(() => {
    if (!formValues || formValues.manufacturer || !manufacturerOptions[0]) {
      return;
    }
    dispatch(
      actions.updateVehicleDetailsFormField({
        field: "manufacturer",
        value: manufacturerOptions[0][0],
      })
    );
  }, [dispatch, formValues, manufacturerOptions]);

  useEffect(() => {
    if (!formValues || formValues.model || !manufacturerModelOptions[0]) {
      return;
    }
    dispatch(
      actions.updateVehicleDetailsFormField({
        field: "model",
        value: manufacturerModelOptions[0][0],
      })
    );
  }, [dispatch, formValues, manufacturerModelOptions]);

  const dataFields = VEHICLE_DATA_FIELDS.map((field) => {
    if (field.field === "manufacturer") {
      return {
        ...field,
        options: manufacturerOptions,
        onMenuScroll: onManufacturerMenuScroll,
      };
    }
    if (field.field === "model") {
      return {
        ...field,
        options: manufacturerModelOptions,
        onMenuScroll: onManufacturerModelMenuScroll,
      };
    }
    return field;
  });

  const configFields = VEHICLE_CONFIG_FIELDS.map((field) => {
    if (field.field === "driverCab") {
      return { ...field, options: VEHICLE_DRIVER_CAB_OPTIONS };
    }
    if (field.field === "drive") {
      return { ...field, options: VEHICLE_DRIVE_OPTIONS };
    }
    if (field.field === "rearSuspension") {
      return { ...field, options: VEHICLE_REAR_SUSPENSION_OPTIONS };
    }
    return field;
  });

  if (!formValues) {
    return null;
  }

  return (
    <>
      <FormSection titleKey={`${formPrefix}.SECTIONS.VEHICLE_DATA`} hideTopBorder>
        <Box className={styles.grid5v}>
          {renderFieldControls(dataFields, formValues, updateField)}
        </Box>
      </FormSection>

      <FormSection titleKey={`${formPrefix}.SECTIONS.VEHICLE_CONFIG`}>
        <Box className={styles.grid5}>
          {renderFieldControls(configFields, formValues, updateField)}
        </Box>
      </FormSection>

      <FormSection titleKey={`${formPrefix}.SECTIONS.CHASSIS_DATA`}>
        <Box className={styles.row}>
          <Box className={styles.grid7}>
            {renderFieldControls(CHASSIS_DATA_FIELDS, formValues, updateField)}
          </Box>
          <Box className={styles.chassisActions}>
            <Box className={styles.chassisButtons}>
              <Button className={styles.btn} variant="outlined" color="primary">
                {t(`${formPrefix}.BUTTONS.HEIGHT_TABLE`)}
              </Button>
              <Button className={styles.btn} variant="outlined" color="primary">
                {t(`${formPrefix}.BUTTONS.ADD_HEIGHT`)}
              </Button>
            </Box>
          </Box>
        </Box>
      </FormSection>

      <FormSection titleKey={`${formPrefix}.SECTIONS.WEIGHT_DATA`}>
        <Box className={styles.grid5}>
          {renderFieldControls(WEIGHT_DATA_FIELDS, formValues, updateField)}
        </Box>
      </FormSection>

      <FormSection titleKey={`${formPrefix}.SECTIONS.ADDITIONS`}>
        <Box className={styles.additions}>
          <Box className={styles.additionsField}>
            <FormFieldControl
              config={{ ...VEHICLE_HYDRAULIC_FIELD, options: hydraulicOptions }}
              value={formValues.hydraulic}
              onChange={(value) => updateField("hydraulic", value)}
            />
          </Box>
          <Box className={styles.additionsField}>
            <Box className={styles.checkboxBox}>
              <Checkbox
                disableRipple
                checked={formValues.transferBody}
                onChange={(event) => updateField("transferBody", event.target.checked)}
                icon={<CheckboxOutlineIcon width={24} height={24} />}
                checkedIcon={<CheckboxIcon width={24} height={24} />}
                sx={{ p: 0, width: 24, height: 24 }}
              />
              <Typography className={styles.fieldValue}>
                {t(`${formPrefix}.FIELDS.TRANSFER_BODY`)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </FormSection>
    </>
  );
};

export default VehicleDetails;
